import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { galleriesAPI } from '../services/api';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import Footer from '../components/shared/Footer';
import './GalleryPage.css';

const GalleryPage = () => {
  const { id } = useParams();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [copied, setCopied] = useState(false);
  const thumbnailsRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadGallery = async () => {
      try {
        setLoading(true);
        const response = await galleriesAPI.getById(id);
        if (response.success && response.data) {
          const g = response.data;
          setGallery({
            id: g.id,
            title: g.title,
            thumbnail: g.thumbnail,
            images: (g.images || []).map(img => (typeof img === 'string' ? img : img.url))
          });
        } else {
          setError('Gallery not found.');
        }
      } catch (err) {
        console.error('Error loading gallery:', err);
        setError(err.message || 'Failed to load gallery.');
      } finally {
        setLoading(false);
      }
    };
    loadGallery();
  }, [id]);

  const handleNext = useCallback(() => {
    if (gallery) setCurrentIndex(prev => (prev + 1) % gallery.images.length);
  }, [gallery]);

  const handlePrev = useCallback(() => {
    if (gallery) setCurrentIndex(prev => (prev - 1 + gallery.images.length) % gallery.images.length);
  }, [gallery]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleNext, handlePrev]);

  useEffect(() => {
    if (thumbnailsRef.current && gallery) {
      const thumbEl = thumbnailsRef.current.children[currentIndex];
      if (thumbEl) {
        thumbEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [currentIndex, gallery]);

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // fallback for browsers without clipboard API
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownloadAll = () => {
    const downloadUrl = galleriesAPI.getDownloadUrl(id);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.click();
  };

  return (
    <div className="gallery-page-wrapper">
      <ResponsiveNavbar
        menuItems={[
          { type: 'link', label: 'Veloura', to: '/' },
          { type: 'link', label: 'Shop', to: '/shop' },
          { type: 'link', label: 'Team', to: '/team' },
          { type: 'link', label: 'Careers', to: '/careers' },
          { type: 'link', label: 'Partners', to: '/partners' },
          { type: 'link', label: 'News', to: '/news' },
          { type: 'link', label: 'Account', to: '/account' }
        ]}
        logo="VELOURA"
        logoLink="/"
        scrolled={scrolled}
      />

      <div className="gallery-page">
        {loading && (
          <div className="gallery-page-state">
            <p>Loading gallery...</p>
          </div>
        )}

        {!loading && error && (
          <div className="gallery-page-state">
            <p>{error}</p>
            <Link to="/" className="gallery-back-link">← Back to Home</Link>
          </div>
        )}

        {!loading && !error && gallery && (
          <div className="gallery-page-container">
            <div className="gallery-page-header">
              <Link to="/" className="gallery-back-link">← Back</Link>
              <h1>{gallery.title}</h1>
              <div className="gallery-page-actions">
                <button className="gallery-action-btn" onClick={handleShare}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                  </svg>
                  {copied ? 'Copied!' : 'Share'}
                </button>
                <button className="gallery-action-btn primary" onClick={handleDownloadAll}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Download All (.zip)
                </button>
              </div>
            </div>

            {gallery.images.length === 0 ? (
              <div className="gallery-page-state">
                <p>No images in this gallery.</p>
              </div>
            ) : (
              <>
                <div className="gallery-page-viewer">
                  {gallery.images.length > 1 && (
                    <button className="gallery-page-nav prev" onClick={handlePrev} aria-label="Previous image">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 18L9 12L15 6"/>
                      </svg>
                    </button>
                  )}
                  <div className="gallery-page-image-wrap">
                    <img
                      key={currentIndex}
                      src={gallery.images[currentIndex]}
                      alt={`${gallery.title} — ${currentIndex + 1}`}
                      className="gallery-page-main-image"
                    />
                  </div>
                  {gallery.images.length > 1 && (
                    <button className="gallery-page-nav next" onClick={handleNext} aria-label="Next image">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 18L15 12L9 6"/>
                      </svg>
                    </button>
                  )}
                  <div className="gallery-page-counter">{currentIndex + 1} / {gallery.images.length}</div>
                </div>

                {gallery.images.length > 1 && (
                  <div className="gallery-page-thumbs" ref={thumbnailsRef}>
                    {gallery.images.map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt={`Thumbnail ${i + 1}`}
                        className={`gallery-page-thumb${i === currentIndex ? ' active' : ''}`}
                        onClick={() => setCurrentIndex(i)}
                        draggable={false}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default GalleryPage;
